import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Box, Button, CircularProgress, Menu, MenuItem, MenuList, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import * as React from 'react';
import * as send from '../../../../../../lib/analytics/send';

const popularList1 = [
  { shortName: 'Revenues (TTM)', aggCode: 'REV.TTM', legend: 'Net sales' },
  { shortName: 'Net Income (TTM)', aggCode: 'NETINC.TTM', legend: 'Net income' },
  { shortName: 'Market Cap', aggCode: 'MKTCAP', legend: 'Market Capital' },
  { shortName: 'Price/Earnings', aggCode: 'PE', legend: 'Price to Earnings Ratio' },
  { shortName: 'Price/Sales', aggCode: 'PS', legend: 'Price to Sales Ratio' },
  { shortName: 'EPS, Basic', aggCode: 'EPS', legend: 'Earnings Per Share, Basic' },
  { shortName: 'Return on Equity', aggCode: 'ROE', legend: 'Return on Equity' },
  { shortName: 'Return on Assets', aggCode: 'ROA', legend: 'Return on Assets' },
];

const buttonStyles = {
  '&:hover': { background: 'hsla(236, 96%, 70%, 0.08)', color: '#174c86' },
  p: '4px 10px',
  fontSize: '.8rem',
  textTransform: 'none',
  borderRadius: '5px',
  mr: 0.5,
  boxShadow: 0,
};

export default function PopDropdown({ seriesName, ticker, setLoading, setSeries, loading }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const open = Boolean(anchorEl);

  const visibleSeries = React.useMemo(
    () => (isMobile ? [] : popularList1.slice(0, 3)),
    [isMobile]
  );

  const hiddenSeries = React.useMemo(
    () => (isMobile ? popularList1 : popularList1.slice(3)),
    [isMobile]
  );

  const isInVisible = React.useMemo(
    () => visibleSeries.some((item) => item.shortName === seriesName),
    [visibleSeries, seriesName]
  );

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleCode = React.useCallback(
    (event, selectedItem) => {
      setLoading(true);
      setSeries({
        seriesCode: `XXXXX.${selectedItem.aggCode}`,
        seriesName: selectedItem.shortName,
        legend: selectedItem.legend,
        code: selectedItem.aggCode,
      });
      send.uevent(event, `historical-chart-search-${ticker}.${selectedItem.aggCode}`);
      handleClose();
    },
    [setLoading, setSeries, ticker, handleClose]
  );

  return (
    <React.Fragment>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          borderBottom: '1px solid #dbdce4',
          ml: 'auto',
          borderRadius: '0px',
          flexWrap: 'wrap',
          width: '100%',
          p: 0.7,
          mb: 0.5,
        }}
      >
        {!isInVisible && seriesName ? (
          <Button
            disableElevation
            sx={{ ...buttonStyles, background: 'hsla(236, 96%, 70%, 0.08)', color: '#174c86' }}
            size="small"
            variant="contained"
            aria-current="true"
            endIcon={loading ? <CircularProgress size={10} color="inherit" /> : undefined}
          >
            {seriesName}
          </Button>
        ) : null}
        {visibleSeries.map((d) => {
          const isSelected = d.shortName === seriesName;
          return (
            <Button
              key={d.aggCode}
              className={isSelected ? 'selected toggle-button' : 'toggle-button'}
              sx={{
                ...buttonStyles,
                background: isSelected ? '#e3e8ee' : 'transparent',
                color: isSelected ? '#174c86' : 'inherit',
              }}
              size="small"
              variant={isSelected ? 'contained' : 'text'}
              aria-current={isSelected ? 'true' : undefined}
              onClick={(e) => handleCode(e, d)}
              endIcon={isSelected && loading ? <CircularProgress size={10} color="inherit" /> : undefined}

            >
              {d.shortName}
            </Button>
          );
        })}
        {hiddenSeries.length > 0 && (
          <Button
            disableElevation
            onClick={handleClick}
            size="small"
            sx={{ ...buttonStyles, color: '#174c86', ml: 0.5 }}
            aria-controls={open ? 'options-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            endIcon={<ExpandMoreRoundedIcon />}
          >
            More
          </Button>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="options-menu"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuList>
          {hiddenSeries.map((d) => (
            <MenuItem
              key={d.aggCode}
              dense
              selected={d.shortName === seriesName}
              onClick={(e) => handleCode(e, d)}
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
            >
              {d.shortName}
              {d.shortName === seriesName && loading && (
                <CircularProgress size={10} color="inherit" />
              )}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </React.Fragment>
  );
}